// The felt's weave (design 15 §1.1, UI review V2): a fine grain over the table's green, so the felt reads as cloth and not as a
// printed colour. Drawn once, when the page is idle, from the theme's own light and dark (src/theme.ts), so a theme carries it;
// tiled on layers of its own, so nothing is drawn per frame. Seeded, so every visit's felt is the same
// felt. Its pixels are written straight into the image, a few milliseconds: drawn fibre by fibre on the canvas it was a task of
// 59 ms, over the start-up budget's 50 (design 11 §9).

import { rgbOf } from "../theme";

const SIZE = 128; // pixels in the tile, shown at that size in CSS pixels: a softer grain on a dense screen, as felt is
const MOST = 0.045; // the strongest grain's opacity: three to four per cent on average

export function weaveFelt(): void {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const image = ctx.createImageData(SIZE, SIZE);
    const px = image.data;
    const light = rgbOf("bright");
    const dark = rgbOf("umbra");
    let seed = 0x2545f491;
    const next = () => {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) % 10000) / 10000;
    };
    // Each fibre a short run along the row or the column, as threads are, at a random strength, lighter or darker.
    for (let i = 0; i < SIZE * SIZE * 0.6; i++) {
      const x = Math.floor(next() * SIZE);
      const y = Math.floor(next() * SIZE);
      const across = next() < 0.5;
      const len = 1 + Math.floor(next() * 3);
      const [r, g, b] = next() < 0.5 ? light : dark;
      const a = Math.round(next() * MOST * 255);
      for (let k = 0; k < len; k++) {
        const at = (((across ? y : (y + k) % SIZE) * SIZE + (across ? (x + k) % SIZE : x)) * 4);
        px[at] = r!;
        px[at + 1] = g!;
        px[at + 2] = b!;
        px[at + 3] = a;
      }
    }
    ctx.putImageData(image, 0, 0);
    // A rule for the two layers alone, which also makes them layers of their own (kept by the compositor, never repainted under a
    // flight) only once there is a weave to keep: a custom property set on the root would restyle every element, a task over 50 ms.
    const style = document.createElement("style");
    style.textContent = `.play::before, .board .surface::after { background-image: url(${canvas.toDataURL("image/png")}); will-change: transform; }`;
    document.head.append(style);
  } catch {
    // no canvas: the felt stays plain
  }
}

let asked = false;
/** The weave, once, when a table is on the page and the page has loaded and is idle: never in the way of the table's first tiles,
 *  and never for the signed-out page's dimmed table behind its words, where it was a task of 52 ms (design 11 §9). */
export function weaveSoon(): void {
  if (asked) return;
  asked = true;
  const idle = () => (typeof requestIdleCallback === "function" ? requestIdleCallback(weaveFelt, { timeout: 5000 }) : setTimeout(weaveFelt, 2000));
  if (document.readyState === "complete") idle();
  else addEventListener("load", idle, { once: true });
}
